import CloneVoiceWizardModal from '@/components/Modals/CloneVoiceWizardModal/CloneVoiceWizardModal';
import { useGetVoicesSeats } from '@/components/Modals/CloneVoiceWizardModal/useGetVoicesSeats';
import SingUpModal from '@/components/Modals/SingUpModal';
import UpgradePlanModal from '@/components/Modals/UpgradePlanModal';
import AdornmentSelect from '@/components/Selects/AdornmentSelect';
import { useAllVoices } from '@/hooks/useAllVoices';
import CloneVoice from '@/images/CloneVoice.svg';
import female from '@/images/Female.svg';
import male from '@/images/Male.svg';
import SoundWave from '@/images/Soundwave.png';
import thirdPartyVoices from '@/images/upgrades/thirdPartyVoices.svg';
import voicePremiumUpgrade from '@/images/upgrades/voicePremiumUpgrade.svg';
import { isProPlus } from '@/pages/AdvancedEditor/components/panels/script/utils';
import { useGetAllVoices, useGetVoiceOptions } from '@/pages/agents/hooks';
import { isMsDeprecatedVoice } from '@/pages/agents/utils';
import {
    DEFAULT_SAMPLE_VOICE_SCRIPT,
    LANGUAGE_TO_SAMPLE_SCRIPT_MAP,
    MS_DEPRECATED_VOICES_MAP,
} from '@/pages/campaign/CreatePage/constants';
import { AddVoiceModal } from '@/pages/editor/components/modals/AddVoiceModal';
import { DeprecatedVoiceModal } from '@/pages/editor/layout/DeprecatedVoiceModal';
import { useAuth } from '@/services/auth';
import {
    cloneVoiceWizardModal as _cloneVoiceWizardModal,
    cloneVoiceUpgradePlanModal,
    ttsSignupModal,
    ttsUpgradePlanModal,
    ttsVoiceModal,
    ttsVoiceSourceFilter,
} from '@/state/presenters';
import { useAgentEditorStore } from '@/state/root';
import { isNonPremiumUser, toCamelCase } from '@/utils/utils';
import { IVoice, Providers, VoiceAccess } from '@d-id/types';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ListboxComponent } from './ListboxComponent';
import { Listen } from './Listen';

export const supported11labsVoices: { language: string; locale: string }[] = [
    { language: 'Chinese', locale: 'zh-CN' },
    { language: 'Korean (Korea)', locale: 'ko-KR' },
    { language: 'Dutch (Netherlands)', locale: 'nl-NL' },
    { language: 'Turkish (Turkey)', locale: 'tr-TR' },
    { language: 'Swedish (Sweden)', locale: 'sv-SE' },
    { language: 'Indonesian (Indonesia)', locale: 'id-ID' },
    { language: 'Filipino (Philippines)', locale: 'fil-PH' },
    { language: 'Japanese (Japan)', locale: 'ja-JP' },
    { language: 'Ukrainian (Ukraine)', locale: 'uk-UA' },
    { language: 'Greek (Greece)', locale: 'el-GR' },
    { language: 'Czech (Czechia)', locale: 'cs-CZ' },
    { language: 'Finnish (Finland)', locale: 'fi-FI' },
    { language: 'Romanian (Romania)', locale: 'ro-RO' },
    { language: 'Russian (Russia)', locale: 'ru-RU' },
    { language: 'Danish (Denmark)', locale: 'da-DK' },
    { language: 'Bulgarian (Bulgaria)', locale: 'bg-BG' },
    { language: 'Malay (Malaysia)', locale: 'ms-MY' },
    { language: 'Slovak (Slovakia)', locale: 'sk-SK' },
    { language: 'Croatian (Croatia)', locale: 'hr-HR' },
    { language: 'Tamil', locale: 'ta-IN' },
    { language: 'English (United States)', locale: 'en-US' },
    { language: 'Polish (Poland)', locale: 'pl-PL' },
    { language: 'German (Germany)', locale: 'de-DE' },
    { language: 'Spanish (Spain)', locale: 'es-ES' },
    { language: 'French (France)', locale: 'fr-FR' },
    { language: 'Italian (Italy)', locale: 'it-IT' },
    { language: 'Hindi (India)', locale: 'hi-IN' },
    { language: 'Portuguese (Portugal)', locale: 'pt-PT' },
    { language: 'English', locale: 'en-US' },
    { language: 'Arabic', locale: 'ar' },
];

interface Props {
    labelOff?: boolean;
    language?: string;
}

const genderImage: Record<string, string> = {
    female,
    male,
    'non-binary': CloneVoice,
};

function getStartAdornment(value: string | IVoice | null, options: IVoice[]): JSX.Element | null {
    if (!value || !options?.length) {
        return null;
    }

    const gender = typeof value === 'string' ? options.find(voice => voice.name === value)?.gender ?? '' : value.gender;
    return gender && gender !== 'unspecified' ? (
        <img loading="lazy" width="20" height="20" src={genderImage[gender.toLowerCase()]} alt="start adornment" />
    ) : (
        <img loading="lazy" width="20" height="20" src={CloneVoice} alt="start adornment" />
    );
}

interface Props {
    children?: React.ReactNode;
}

const isImportedElevenLabsVoice = (voice: IVoice) => {
    return voice.provider === Providers.Elevenlabs && voice.access === VoiceAccess.ExternalPrivate;
};

export const VoicesSelect = observer(({ labelOff = false }: Props) => {
    const agentEditorStore = useAgentEditorStore();

    const [signupModalState, setSignupModalState] = useRecoilState(ttsSignupModal);
    const [voiceModalState] = useRecoilState(ttsVoiceModal);
    const [cloneVoiceWizardModal, setCloneVoiceWizardModal] = useRecoilState(_cloneVoiceWizardModal);
    const [upgradePlanModalState, setUpgradePlanModalState] = useRecoilState(ttsUpgradePlanModal);
    const [showCloneVoiceUpgradeModal, setShowCloneVoiceUpgradeModal] = useRecoilState(cloneVoiceUpgradePlanModal);
    const [showUpgradeVoiceModal, setShowUpgradeVoiceModal] = useState(false);
    const [deprecatedVoiceState, setDeprecatedVoiceState] = useState(false);
    const { isAnonymous, user } = useAuth();
    const userPlan = user?.planGroup ?? user?.productName;
    const options = useGetVoiceOptions();
    const filter = useRecoilValue(ttsVoiceSourceFilter);
    const { refetch } = useAllVoices(false, [Providers.Elevenlabs, Providers.Microsoft, Providers.AzureOpenAi]);
    useGetAllVoices();
    useGetVoicesSeats();

    useEffect(() => {
        setDeprecatedVoiceState(isMsDeprecatedVoice(agentEditorStore.ttsSelectedVoice));
    }, [agentEditorStore.ttsSelectedVoice, setDeprecatedVoiceState]);

    const filteredOptions =
        filter === 'default'
            ? options.filter(option => !isImportedElevenLabsVoice(option))
            : options.filter(option => isImportedElevenLabsVoice(option));
    const filteredHDVoices = useMemo(() => {
        let newVoiceList: IVoice[] = [];
        filteredOptions.forEach((voice: IVoice) => {
            if (
                ![Providers.Microsoft, Providers.AzureOpenAi].includes(voice.provider) ||
                (voice.name.indexOf(' HD ') == -1 && !voice.name.endsWith(' HD'))
            ) {
                newVoiceList.push(voice);
            }
        });
        return newVoiceList;
    }, [filteredOptions]);
    const onCloseModal = () => setUpgradePlanModalState(false);

    const onAdditionalVoice = async () => {
        await refetch();
    };

    const getUpgradePlanModal = () =>
        upgradePlanModalState ? (
            <UpgradePlanModal
                open={!isAnonymous && upgradePlanModalState}
                onClose={onCloseModal}
                title={<div>Upgrade to import 3rd party voices</div>}
                imageFile={thirdPartyVoices}
                from="voice-select"
                features={['Additional sessions', 'Premium avatars', 'Premium voices']}
            />
        ) : (
            showUpgradeVoiceModal && (
                <UpgradePlanModal
                    open={showUpgradeVoiceModal}
                    onClose={() => setShowUpgradeVoiceModal(false)}
                    title={<div>Upgrade to get premium voices</div>}
                    imageFile={voicePremiumUpgrade}
                    from="voice-select"
                    features={['Additional sessions', '3rd party integrations', 'Premium avatars']}
                />
            )
        );

    return (
        <>
            <SingUpModal isOpen={isAnonymous && upgradePlanModalState} onClose={onCloseModal} from="agent-voice-select" />
            {getUpgradePlanModal()}
            {voiceModalState && <AddVoiceModal refetchVoices={onAdditionalVoice} />}
            {cloneVoiceWizardModal && (
                <CloneVoiceWizardModal
                    open={cloneVoiceWizardModal}
                    onClose={() => setCloneVoiceWizardModal(false)}
                    onClone={onAdditionalVoice}
                    onSuccess={() => setCloneVoiceWizardModal(false)}
                />
            )}

            <AdornmentSelect
                listboxComponent={(props: any) => <ListboxComponent {...props} />}
                noOptionsText={<ListboxComponent />}
                id="voice-selects"
                label={labelOff ? '' : 'Voices'}
                isProPlus={voice => filter === 'default' && isProPlus(voice)}
                options={filteredHDVoices}
                value={agentEditorStore.ttsSelectedVoice}
                groupBy={
                    filter === 'default'
                        ? (option: IVoice | null) =>
                              option?.gender
                                  ? `${toCamelCase(option.gender)} voices`
                                  : option?.provider === Providers.Elevenlabs && option.config
                                    ? 'Cloned voices'
                                    : ''
                        : () => 'IIElevenLabs voices'
                }
                onChange={(_, value) => {
                    if (isAnonymous && value?.access === VoiceAccess.Premium) {
                        setUpgradePlanModalState(true);
                        return;
                    }

                    if (!isAnonymous && value?.access === VoiceAccess.Premium && isNonPremiumUser(userPlan, true)) {
                        setShowUpgradeVoiceModal(true);
                        return;
                    }

                    runInAction(() => {
                        agentEditorStore.setTtsSelectedVoice(value, true);
                        agentEditorStore.setTtsVoiceConfig((prev: any) => ({ ...prev, style: '' }));
                        agentEditorStore.setErrors(errors => ({ ...errors, ttsError: null }));
                    });
                }}
                getOptionLabel={voice => voice?.name ?? ''}
                getStartAdornment={value => getStartAdornment(value, filteredHDVoices)}
                getEndAdornment={
                    filter === 'default'
                        ? (voice: IVoice | null) => {
                              const voiceLanguage = voice?.language.split(' ')[0].toLowerCase();
                              const sampleScript = voiceLanguage
                                  ? LANGUAGE_TO_SAMPLE_SCRIPT_MAP[voiceLanguage]
                                  : DEFAULT_SAMPLE_VOICE_SCRIPT;
                              return voice && <Listen disabled={false} voice={voice} text={sampleScript} />;
                          }
                        : undefined
                }
            />
            <SingUpModal isOpen={signupModalState} onClose={() => setSignupModalState(false)} />
            <UpgradePlanModal
                open={showCloneVoiceUpgradeModal}
                onClose={() => {
                    setShowCloneVoiceUpgradeModal(false);
                }}
                title={<div>Upgrade to Pro to clone more voices</div>}
                imageFile={SoundWave}
                from="clone-voice"
            />
            <DeprecatedVoiceModal
                isOpen={deprecatedVoiceState}
                onClose={() => {
                    setDeprecatedVoiceState(false);
                    const newVoiceId = MS_DEPRECATED_VOICES_MAP[agentEditorStore.ttsSelectedVoice?.id || ''];
                    const newVoice = options.find(voice => voice?.id === newVoiceId);
                    agentEditorStore.setTtsSelectedVoice(newVoice ?? options[0]);
                }}
            />
        </>
    );
});
